import { Principal } from '@icp-sdk/core/principal'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { config } from '../appConfig'
import { TopUp } from '../clients/TopUp'
import { TCYCLES_CANISTER_ID } from '../constants/canisterIds'
import { _SERVICE as TCycleActor, idlFactory as tcycleIdl } from '../idls/tokens/tcycle.did'
import { GAEventName } from '../interfaces/googleAnalytics'
import { useGoogleAnalytics } from '../providers/GoogleAnalyticsProvider'
import { createActor } from '../utils/createActor'
import { decimalsToE8s } from '../utils/validationHelper'
import useBalances from './useBalances'
import useWallet from './useWallet'

const useCanisterManualTopup = () => {
  const [amount, setAmount] = useState<string>('')
  const [topupLoading, setTopupLoading] = useState(false)

  const { t } = useTranslation()

  const { findBalance, refreshBalance } = useBalances()
  const { wallet } = useWallet()
  const { fireEvent } = useGoogleAnalytics()

  const walletCyclesBalance = findBalance({ address: TCYCLES_CANISTER_ID })
  const tcyclesToken = walletCyclesBalance?.token

  const topUp = async (topupAmount: string, canisterId: string) => {
    if (!tcyclesToken) return

    setTopupLoading(true)

    const agent = config.getAgent({ principalId: wallet.principalId })

    const topupClient = new TopUp(agent)

    const amount = BigInt(decimalsToE8s(topupAmount || '', tcyclesToken.decimals))

    const actor = createActor<TCycleActor>(tcycleIdl, {
      agent,
      canisterId: TCYCLES_CANISTER_ID,
    })

    try {
      const withdrawResponse = await actor.withdraw({
        to: Principal.from(canisterId),
        amount,
        from_subaccount: [],
        created_at_time: [],
      })

      if ('Ok' in withdrawResponse) {
        await topupClient.registerManualTopup(canisterId, amount)
        fireEvent(GAEventName.CYCLE_TOPUP, {
          amount: Number(amount),
        })

        return true
      }

      if ('Err' in withdrawResponse) {
        return Promise.reject(JSON.stringify(withdrawResponse.Err))
      }

      refreshBalance(tcyclesToken)

      return true
    } finally {
      setTopupLoading(false)
    }
  }

  const error = useMemo(() => {
    if (!tcyclesToken) return

    const amounte8s = BigInt(decimalsToE8s(amount, tcyclesToken?.decimals))
    if (!amounte8s) return t('send.errors.noAmount')

    if (amounte8s + tcyclesToken.fee > walletCyclesBalance.balance) {
      return t('send.errors.lowBalance', { token: tcyclesToken.symbol })
    }
  }, [amount, walletCyclesBalance])

  return {
    amount,
    setAmount,
    walletCyclesBalance,
    topUp,
    error,
    topupLoading,
  }
}

export default useCanisterManualTopup
